import React, { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Shield, Lock, Server, Eye, FileCheck, Fingerprint } from 'lucide-react';

const Security = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);

  const features = [
    {
      icon: Server,
      title: 'On-Premise Infrastructure',
      description: 'Your data never leaves your network. All processing happens on Nvidia DGX Spark and Mac Studios within your secure environment.'
    },
    {
      icon: Lock,
      title: 'Private LLM Models',
      description: 'Custom-trained language models running locally ensure your proprietary information stays confidential and secure.'
    },
    {
      icon: Shield,
      title: 'Enterprise Encryption',
      description: 'Military-grade encryption for data at rest and in transit, with multi-layer security protocols protecting every interaction.'
    },
    {
      icon: Eye,
      title: 'Zero Cloud Dependency',
      description: 'Complete independence from third-party cloud services eliminates external vulnerabilities and data exposure risks.'
    },
    {
      icon: FileCheck,
      title: 'Compliance Ready',
      description: 'Built to meet GDPR, CCPA, SOC 2, and industry-specific compliance requirements out of the box.'
    },
    {
      icon: Fingerprint,
      title: 'Access Control',
      description: 'Granular permission systems and audit trails ensure only authorized personnel can access sensitive AI operations.'
    }
  ];

  return (
    <section id="security" ref={sectionRef} className="relative py-24 md:py-32 bg-black overflow-hidden">
      {/* Background Grid */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff05_1px,transparent_1px),linear-gradient(to_bottom,#ffffff05_1px,transparent_1px)] bg-[size:4rem_4rem]"></div>

      <div className="relative z-10 container mx-auto px-6">
        {/* Section Header */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <div
            className={`inline-flex items-center space-x-2 bg-[#FF6600]/10 border border-[#FF6600]/30 rounded-full px-5 py-2 mb-6 transition-all duration-700 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <Shield className="w-4 h-4 text-[#FF6600]" />
            <span className="text-[#FF6600] font-semibold text-sm">Enterprise Security</span>
          </div>
          <h2
            className={`text-4xl md:text-5xl lg:text-6xl font-bold mb-6 transition-all duration-700 delay-100 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Your Data. <span className="text-gradient-orange">Your Infrastructure.</span>
          </h2>
          <p
            className={`text-lg md:text-xl text-gray-400 transition-all duration-700 delay-200 ${
              isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            Keep complete control of your data with local LLM processing. No third-party access, no cloud vulnerabilities, no compromises.
          </p>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          {/* Left - Security Image */}
          <div
            className={`transition-all duration-700 delay-300 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'
            }`}
          >
            <div className="relative rounded-2xl overflow-hidden border border-[#FF6600]/20 shadow-2xl">
              <img
                src="https://images.unsplash.com/photo-1639066648921-82d4500abf1a"
                alt="Server Infrastructure"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent"></div>
              
              {/* Floating Stats */}
              <div className="absolute top-6 right-6 glass-morphism rounded-lg p-4 border border-white/10">
                <div className="text-3xl font-bold text-[#FF6600]">100%</div>
                <div className="text-sm text-gray-300">Data Sovereignty</div>
              </div>
              <div className="absolute bottom-6 left-6 glass-morphism rounded-lg p-4 border border-white/10">
                <div className="text-3xl font-bold text-[#FF6600]">0</div>
                <div className="text-sm text-gray-300">Cloud Dependencies</div>
              </div>
            </div>
          </div>

          {/* Right - Infrastructure Details */}
          <div
            className={`transition-all duration-700 delay-400 ${
              isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'
            }`}
          >
            <h3 className="text-3xl font-bold text-white mb-6">Enterprise-Grade Infrastructure</h3>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                  <Server className="w-6 h-6 text-[#FF6600]" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-2">Nvidia DGX Spark</h4>
                  <p className="text-gray-400">Enterprise AI supercomputing platform delivering unprecedented performance for LLM inference and training.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                  <Server className="w-6 h-6 text-[#FF6600]" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-2">Mac Studio Fleet</h4>
                  <p className="text-gray-400">Apple Silicon architecture optimized for AI workloads with unmatched efficiency and local processing power.</p>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center flex-shrink-0">
                  <Lock className="w-6 h-6 text-[#FF6600]" />
                </div>
                <div>
                  <h4 className="text-xl font-semibold text-white mb-2">Private Network</h4>
                  <p className="text-gray-400">Isolated network infrastructure ensures your AI operations remain completely segregated from external access.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Security Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <Card
                key={index}
                className={`glass-morphism border-white/10 hover-lift transition-all duration-700 group cursor-pointer ${
                  isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
                }`}
                style={{ transitionDelay: `${600 + index * 100}ms` }}
              >
                <CardContent className="p-6">
                  <div className="w-12 h-12 rounded-lg bg-[#FF6600]/20 flex items-center justify-center mb-4 group-hover:bg-[#FF6600]/30 transition-all duration-300">
                    <Icon className="w-6 h-6 text-[#FF6600]" />
                  </div>
                  <h3 className="text-lg font-bold text-white mb-3 group-hover:text-[#FF6600] transition-colors duration-300">
                    {feature.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Security;
